import type { AddressFragment, CreateAddressInput, CustomerFragment, RequestUpdateCustomerEmailAddressMutationVariables, UpdateAddressInput, UpdateCustomerEmailAddressMutationVariables, UpdateCustomerInput, UpdateCustomerPasswordMutationVariables } from '#graphql-operations'
import { ErrorCode } from '#graphql-operations'
import { EmailAddressConflictError, IdentifierChangeTokenExpiredError, IdentifierChangeTokenInvalidError, InvalidCredentialsError, NativeAuthStrategyError, PasswordValidationError } from '@layers/common/app/services/errors'

export function useActiveCustomer() {
  const state = useStore()
  const { refreshActiveCustomer } = useAuth()

  const updateCustomer = async (body: UpdateCustomerInput) => {
    const customer = await useGraphqlMutation('updateCustomer', { input: body }).then(result => result.data)

    if (state.value.activeCustomer)
      Object.assign(state.value.activeCustomer, customer.updateCustomer)
    else
      state.value.activeCustomer = customer.updateCustomer

    // Refresh the auth plugin's activeCustomer to sync with the updated profile
    await refreshActiveCustomer()
  }

  const requestUpdateCustomerEmailAddress = async (body: RequestUpdateCustomerEmailAddressMutationVariables) => {
    const { data } = await useGraphqlMutation('requestUpdateCustomerEmailAddress', body)

    if (isGraphqlError(data.requestUpdateCustomerEmailAddress)) {
      switch (data.requestUpdateCustomerEmailAddress.errorCode) {
        case ErrorCode.EMAIL_ADDRESS_CONFLICT_ERROR: {
          throw new EmailAddressConflictError(data.requestUpdateCustomerEmailAddress.message)
        }
        case ErrorCode.INVALID_CREDENTIALS_ERROR: {
          throw new InvalidCredentialsError(data.requestUpdateCustomerEmailAddress.message, data.requestUpdateCustomerEmailAddress.authenticationError)
        }
        case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
          throw new NativeAuthStrategyError(data.requestUpdateCustomerEmailAddress.message)
        }
        default: {
          throw new Error(data.requestUpdateCustomerEmailAddress.message)
        }
      }
    }

    if (isGraphqlType(data.requestUpdateCustomerEmailAddress, 'Success')) {
      return data.requestUpdateCustomerEmailAddress.success
    }

    throw new Error('Unknown error')
  }

  const updateCustomerEmailAddress = async (body: UpdateCustomerEmailAddressMutationVariables) => {
    const { data } = await useGraphqlMutation('updateCustomerEmailAddress', body)

    // The result is a union: an expired or invalid change token used to be
    // ignored here, so the page reported success and redirected to the account
    // while the address had not actually changed.
    if (isGraphqlError(data.updateCustomerEmailAddress)) {
      switch (data.updateCustomerEmailAddress.errorCode) {
        case ErrorCode.IDENTIFIER_CHANGE_TOKEN_EXPIRED_ERROR: {
          throw new IdentifierChangeTokenExpiredError(data.updateCustomerEmailAddress.message)
        }
        case ErrorCode.IDENTIFIER_CHANGE_TOKEN_INVALID_ERROR: {
          throw new IdentifierChangeTokenInvalidError(data.updateCustomerEmailAddress.message)
        }
        case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
          throw new NativeAuthStrategyError(data.updateCustomerEmailAddress.message)
        }
        default: {
          throw new Error(data.updateCustomerEmailAddress.message)
        }
      }
    }

    state.value.activeCustomer = undefined
  }

  const updateCustomerPassword = async (body: UpdateCustomerPasswordMutationVariables) => {
    const { data } = await useGraphqlMutation('updateCustomerPassword', body)

    if (isGraphqlError(data.updateCustomerPassword)) {
      switch (data.updateCustomerPassword.errorCode) {
        case ErrorCode.INVALID_CREDENTIALS_ERROR: {
          throw new InvalidCredentialsError(data.updateCustomerPassword.message, data.updateCustomerPassword.authenticationError)
        }
        case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
          throw new NativeAuthStrategyError(data.updateCustomerPassword.message)
        }
        case ErrorCode.PASSWORD_VALIDATION_ERROR: {
          throw new PasswordValidationError(data.updateCustomerPassword.message, data.updateCustomerPassword.validationErrorMessage)
        }
        default: {
          throw new Error(data.updateCustomerPassword.message)
        }
      }
    }

    return data.updateCustomerPassword
  }

  const createCustomerAddress = async (body: CreateAddressInput) => {
    const { data } = await useGraphqlMutation('createCustomerAddress', { input: body })
    state.value.addresses ??= []
    state.value.addresses.push(data.createCustomerAddress)
  }

  const deleteCustomerAddress = async (id: string) => {
    await useGraphqlMutation('deleteCustomerAddress', { id })
    state.value.addresses = state.value.addresses?.filter(address => address.id !== id)
  }

  const updateCustomerAddress = async (body: UpdateAddressInput) => {
    const { data } = await useGraphqlMutation('updateCustomerAddress', { input: body })
    const index = state.value.addresses?.findIndex(address => address.id === data.updateCustomerAddress.id)
    if (index !== undefined)
      state.value.addresses?.splice(index, 1, data.updateCustomerAddress)
  }

  return {
    activeCustomer: computed(() => state.value.activeCustomer),
    addresses: computed(() => state.value.addresses),
    updateCustomer,
    requestUpdateCustomerEmailAddress,
    updateCustomerEmailAddress,
    updateCustomerPassword,
    createCustomerAddress,
    deleteCustomerAddress,
    updateCustomerAddress,
  }
}

export function getCustomerFullName(customer: Omit<CustomerFragment, 'id'> | undefined | null, address: (Omit<AddressFragment, 'id' | 'country' | 'streetLine1'> & {
  streetLine1?: string | null
  countryCode?: string | null
}) | undefined | null) {
  return address?.fullName ? address.fullName : `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
}

export function getCustomerPhoneNumber(customer: Omit<CustomerFragment, 'id'> | undefined | null, address: (Omit<AddressFragment, 'id' | 'country' | 'streetLine1'> & {
  streetLine1?: string | null
  countryCode?: string | null
}) | undefined | null) {
  return address?.phoneNumber || customer?.phoneNumber || ''
}
